import React from 'react';
import { motion } from 'framer-motion';
import { Instagram, Youtube, Twitter } from 'lucide-react';

const socialLinks = [
  { name: 'Instagram', icon: Instagram, href: 'https://www.instagram.com/sugosoundmusic' },
  { name: 'YouTube', icon: Youtube, href: '#' },
  { name: 'Twitter', icon: Twitter, href: '#' },
];

export const Footer = ({ albums = [] }) => {
  const footerLinks = [
    {
      title: 'Navigate',
      links: [
        { name: 'Drops', href: '#drops' },
        { name: 'Worlds', href: '#worlds' },
        { name: 'Listen', href: '#listen' },
        { name: 'Universe', href: '#universe' },
      ],
    },
    {
      title: 'Releases',
      // Link to Hyperfollow for each album (shows all platform options)
      links: albums.map(a => ({ 
        name: a.title, 
        href: a.streamingLinks?.hyperfollow || a.streamingLinks?.spotify || '#',
        external: true 
      })),
    },
    {
      title: 'Legal',
      links: [
        { name: 'Privacy', href: '#' },
        { name: 'Terms', href: '#' },
      ],
    },
  ];

  return (
    <footer className="relative pt-16 sm:pt-20 pb-6 sm:pb-8 overflow-hidden">
      <div className="absolute inset-0 bg-background" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-crimson/30 to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 gap-8 sm:gap-12 lg:gap-8 mb-12 sm:mb-16">
          {/* Brand */}
          <div className="col-span-2 sm:col-span-2 lg:col-span-2">
            <motion.a
              href="#"
              className="inline-flex items-center gap-3 mb-4 sm:mb-6 group"
              whileHover={{ scale: 1.02 }}
            >
              <div className="relative">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-crimson flex items-center justify-center">
                  <span className="font-display text-background text-xl sm:text-2xl font-bold">S</span>
                </div>
              </div>
              <div>
                <span className="font-display text-xl sm:text-2xl text-ivory tracking-wider">SUGO</span>
                <span className="font-display text-xl sm:text-2xl text-crimson ml-1 tracking-wider">SOUND</span>
              </div>
            </motion.a>

            <p className="text-ivory/40 font-body text-xs sm:text-sm leading-relaxed max-w-sm mb-6 sm:mb-8">
              Italian soul. Latin rhythm. Nightlife energy.
              A cinematic sound universe.
            </p>

            {/* Socials */}
            <div className="flex items-center gap-3">
              {socialLinks.map((social) => (
                <motion.a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1, y: -2 }}
                  className="w-9 h-9 sm:w-10 sm:h-10 border border-border/30 flex items-center justify-center text-muted-foreground hover:text-crimson hover:border-crimson/50 transition-all duration-400"
                >
                  <social.icon className="w-4 h-4" />
                </motion.a>
              ))}
            </div>
          </div>

          {/* Links */}
          {footerLinks.map((column) => (
            <div key={column.title}>
              <h4 className="text-[10px] font-body uppercase tracking-[0.25em] text-crimson mb-4 sm:mb-6">
                {column.title}
              </h4>
              <ul className="space-y-2 sm:space-y-3">
                {column.links.map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.href}
                      target={link.external ? '_blank' : undefined}
                      rel={link.external ? 'noopener noreferrer' : undefined}
                      className="text-xs sm:text-sm text-ivory/40 hover:text-crimson font-body transition-colors duration-300"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom */}
        <div className="pt-6 sm:pt-8 border-t border-border/20">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <p className="text-[10px] text-muted-foreground font-body tracking-wider text-center">
              © {new Date().getFullYear()} SUGO SOUND. ALL RIGHTS RESERVED.
            </p>
          </div>
        </div>

        {/* Large background text */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 pointer-events-none select-none overflow-hidden">
          <span className="font-display text-[12vw] sm:text-[15vw] leading-none text-charcoal/20 whitespace-nowrap tracking-wider">
            SUGO
          </span>
        </div>
      </div>
    </footer>
  );
};
